import m from "mithril";
import Layout from "./Layout.js";
import { getMithril, showToast } from "../utils.js";

var AccountList = {
    oninit: function (vnode) {
        this.accounts = [];
        this.isLoading = false;
        this.error = null;
        this.searchQuery = "";
        this.loadAccounts();
    },

    loadAccounts: async function () {
        try {
            console.log("Loading accounts...");
            const url = this.searchQuery
                ? `/api/savings-accounts?search=${encodeURIComponent(this.searchQuery)}`
                : "/api/savings-accounts";

            const response = await getMithril(url, { component: this });
            console.log("API response received:", response);

            // API returns { success: true, data: [...], count: n }
            if (response && response.success && response.data) {
                const data = response.data;
                console.log("Data array:", data);

                // Enhance account data with customer type information
                this.accounts = (data || []).map(account => ({
                    ...account,
                    customer_type: account.customer_type || 'individual', // Default to individual if not specified
                    customer_type_display: account.customer_type === 'kelompok' ? 'Kelompok' : 'Individu'
                }));
                console.log(`Loaded ${this.accounts.length} accounts:`, this.accounts);
            } else {
                console.error("Invalid API response structure:", response);
                this.accounts = [];
                this.error = "Invalid response format from server";
            }

            this.error = null; // Clear any previous errors if we got here
        } catch (e) {
            this.error = "Error loading accounts: " + e.message;
            console.error("Account loading error:", e);
            this.accounts = [];
        } finally {
            this.isLoading = false;
            m.redraw();
        }
    },

    viewAccount: function (account) {
        console.log("Viewing account:", account);
        // Navigate to view page with account ID
        m.route.set(`/view-account/${account.id}`);
    },

    depositToAccount: function (account) {
        console.log("Depositing to account:", account);
        // Navigate to deposit page with account ID
        m.route.set(`/savings-deposits?account=${account.id}`);
    },

    withdrawFromAccount: function (account) {
        console.log("Withdrawing from account:", account);
        // Navigate to withdrawal page with account ID
        m.route.set(`/savings-withdrawals?account=${account.id}`);
    },

    formatCurrency: function (amount) {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR'
        }).format(amount || 0);
    },

    formatDate: function (dateString) {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('id-ID', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    },

    view: function (vnode) {
        return [
            m("div", { class: "container mx-auto p-4" }, [
                m("h1", { class: "text-2xl font-bold mb-4" }, "Daftar Rekening Simpanan"),

                // Header with search and refresh button
                m("div", { class: "flex items-center justify-between mb-4" }, [
                    m("p", { class: "text-gray-600 dark:text-gray-400" },
                        `Total ${this.accounts.length} rekening tersimpan`
                    ),
                    m("div", { class: "flex space-x-2" }, [
                        m("input", {
                            type: "text",
                            class: "px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white",
                            placeholder: "Cari nomor rekening/nama...",
                            value: this.searchQuery,
                            oninput: (e) => this.searchQuery = e.target.value,
                            onkeypress: (e) => {
                                if (e.key === 'Enter') this.loadAccounts();
                            }
                        }),
                        m("button", {
                            class: "bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded",
                            onclick: () => this.loadAccounts()
                        }, "Cari"),
                        m("button", {
                            class: "bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600",
                            onclick: () => this.loadAccounts()
                        }, "🔄")
                    ])
                ]),

                // Loading spinner
                this.isLoading ? m("div", {
                    class: "flex items-center justify-center p-8 bg-white dark:bg-gray-800",
                }, [
                    m("div", { class: "flex items-center space-x-2 text-gray-600 dark:text-gray-300" }, [
                        m("div", { class: "animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2" }),
                        m("span", "Memuat data rekening...")
                    ])
                ]) : null,

                // Error message
                this.error && !this.isLoading ? m("div", {
                    class: "bg-red-100 dark:bg-red-900 border border-red-400 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4"
                }, this.error) : null,

                // Accounts table
                !this.isLoading && !this.error && this.accounts.length > 0 ?
                    m("div", { class: "bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden" }, [
                        m("table", { class: "min-w-full divide-y divide-gray-200 dark:divide-gray-700" }, [
                            // Table header
                            m("thead", { class: "bg-gray-50 dark:bg-gray-700" }, [
                                m("tr", [
                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Nomor Rekening"),
                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Nama Rekening"),
                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Nasabah"),
                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Tipe Nasabah"),
                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Saldo"),
                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Tanggal Pembukaan"),
                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Status"),
                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Aksi")
                                ])
                            ]),
                            // Table body
                            m("tbody", { class: "bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" },
                                this.accounts.map(account => {
                                    return m("tr", { class: "hover:bg-gray-50 dark:hover:bg-gray-700" }, [
                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" },
                                            account.account_number
                                        ),
                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300" },
                                            account.account_name || '-'
                                        ),
                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300" },
                                            account.customer_name || '-'
                                        ),
                                        m("td", { class: "px-6 py-4 whitespace-nowrap" }, [
                                            m("span", {
                                                class: `inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${account.customer_type === 'kelompok'
                                                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                                    }`
                                            }, [
                                                account.customer_type === 'kelompok' ? m("i", { class: "ri-group-line mr-1" }) : m("i", { class: "ri-user-line mr-1" }),
                                                account.customer_type_display
                                            ])
                                        ]),
                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300" },
                                            this.formatCurrency(account.balance)
                                        ),
                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300" },
                                            this.formatDate(account.opening_date)
                                        ),
                                        m("td", { class: "px-6 py-4 whitespace-nowrap" }, [
                                            m("span", {
                                                class: `inline-flex px-2 py-1 text-xs font-semibold rounded-full ${account.status === 'active'
                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                                    }`
                                            }, account.status === 'active' ? 'Aktif' : 'Tidak Aktif')
                                        ]),
                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2" }, [
                                            m("button", {
                                                class: "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300",
                                                onclick: () => this.viewAccount(account),
                                                title: "Lihat detail rekening"
                                            }, "👁️"),
                                            m("button", {
                                                class: "text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300",
                                                onclick: () => this.depositToAccount(account),
                                                title: "Setor ke rekening"
                                            }, "📥"),
                                            m("button", {
                                                class: "text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300",
                                                onclick: () => this.withdrawFromAccount(account),
                                                title: "Tarik dari rekening"
                                            }, "📤")
                                        ])
                                    ]);
                                })
                            )
                        ])
                    ]) : null,

                // Empty state
                !this.isLoading && !this.error && this.accounts.length === 0 ?
                    m("div", { class: "text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow" }, [
                        m("svg", {
                            class: "mx-auto h-12 w-12 text-gray-400",
                            fill: "none",
                            viewBox: "0 0 24 24",
                            stroke: "currentColor"
                        }, [
                            m("path", {
                                "stroke-linecap": "round",
                                "stroke-linejoin": "round",
                                "stroke-width": "2",
                                d: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            })
                        ]),
                        m("h3", { class: "mt-2 text-sm font-medium text-gray-900 dark:text-white" }, "Belum ada rekening"),
                        m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Rekening simpanan yang dibuat akan muncul di sini."),
                        m("div", { class: "mt-6" }, [
                            m("button", {
                                type: "button",
                                class: "inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
                                onclick: () => m.route.set('/new-account')
                            }, [
                                m("svg", { class: "-ml-1 mr-2 h-5 w-5", viewBox: "0 0 20 20", fill: "currentColor" }, [
                                    m("path", { "fill-rule": "evenodd", d: "M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z", "clip-rule": "evenodd" })
                                ]),
                                "Buat Rekening Baru"
                            ])
                        ])
                    ]) : null
            ])
        ];
    }
};

export default AccountList;